import React, { useState } from 'react';
import styled from 'styled-components';
import TextFieldWithInfo from 'components/TextFieldWithInfo';
import { Button, Card, CardContent, Typography } from '@material-ui/core';
import { ICourseKLineConfig } from '../../../components/CourseKLineSelector';
import DialectSelect, { IDialectOption } from 'components/selects/DialectSelect';
import RefSelectorList from '../../../components/RefSelectorList';
import MemoryTranslationFilter from './common/MemoryTranslationFilter';
import { TRANSLATION_REF_OPTIONS } from '../constants/translationRefOptions';

export interface IMemorySemanticUnifierConfig {
  general: {
    translation_refs: number[];
    source_dialect: number;
    threshold?: number;
    topK?: number;
  };
  filtering: {
    courses: ICourseKLineConfig[];
    only_published_lessons?: boolean;
  };
}

interface Props {
  onStartGuessing: (config: IMemorySemanticUnifierConfig) => void;
  className?: string;
}

const defaultConfig: IMemorySemanticUnifierConfig = {
  general: {
    translation_refs: TRANSLATION_REF_OPTIONS.map((opt) => opt.value),
    source_dialect: 0,
    threshold: 0.82,
    topK: 50,
  },
  filtering: {
    courses: [],
  },
};

const MemorySemanticUnifierConfig: React.FC<Props> = ({ onStartGuessing, className }) => {
  const [config, setConfig] = useState<IMemorySemanticUnifierConfig>(defaultConfig);

  const handleTranslationRefsChange = (refs: number[]) => {
    setConfig((prev) => ({
      ...prev,
      general: {
        ...prev.general,
        translation_refs: refs,
      },
    }));
  };

  const handleDialectChange = (opt: IDialectOption) => {
    setConfig((prev) => ({
      ...prev,
      general: { ...prev.general, source_dialect: opt.dialectId },
    }));
  };

  const handleCoursesChange = (courses: ICourseKLineConfig[]) => {
    setConfig((prev) => ({
      ...prev,
      filtering: {
        ...prev.filtering,
        courses,
      },
    }));
  };

  const handleStartGuessing = () => {
    onStartGuessing(config);
  };

  const canStart =
    (config.filtering.courses?.length ?? 0) > 0 &&
    (config.general.source_dialect ?? 0) > 0 &&
    (config.general.translation_refs?.length ?? 0) > 0;

  return (
    <Wrapper className={className}>
      <Header>
        <HeaderLeft>
          <Title>Semantic Unifier Configuration</Title>
          <Description>Configure parameters for semantic unification from translations.</Description>
        </HeaderLeft>
        <HeaderRight></HeaderRight>
      </Header>
      <FullWidthWrapper>
        <FullWidthConfigCard>
          <CardContent>
            <CollapsibleContent>
              <MemoryTranslationFilter
                valueCourses={config.filtering.courses}
                onCoursesChange={handleCoursesChange}
                valueOnlyPublished={!!config.filtering.only_published_lessons}
                onOnlyPublishedChange={(checked) =>
                  setConfig((prev) => ({ ...prev, filtering: { ...prev.filtering, only_published_lessons: checked } }))
                }
                // keep dialect and refs controls in General Parameters to preserve current layout
                showDialect={false}
                showRefs={false}
              />
            </CollapsibleContent>
          </CardContent>
        </FullWidthConfigCard>
      </FullWidthWrapper>
      <FullWidthWrapper>
        <FullWidthConfigCard>
          <CardContent>
            <SectionTitle>General Parameters</SectionTitle>
            <TwoColumnGrid>
              <ColumnSection>
                <DialectSelect onlySource handleDialectChange={handleDialectChange} />
              </ColumnSection>
              <ColumnSection>
                <RefSelectorList value={config.general.translation_refs} onChange={handleTranslationRefsChange} options={TRANSLATION_REF_OPTIONS} label="Translation Sources" />
              </ColumnSection>
            </TwoColumnGrid>
            <TwoColumnGrid>
              <ColumnSectionSpaced>
                <TextFieldWithInfo
                  label="Threshold"
                  type="number"
                  inputProps={{ step: 0.01, min: 0.5, max: 0.99 }}
                  value={config.general.threshold ?? 0.82}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const raw = e.target.value;
                    const parsed = parseFloat(raw);
                    const next = Number.isFinite(parsed) ? parsed : 0.82;
                    setConfig((prev) => ({
                      ...prev,
                      general: { ...prev.general, threshold: next },
                    }));
                  }}
                  tooltip="Similarity cutoff (0–1). Higher values produce tighter groups. Default 0.82."
                />
              </ColumnSectionSpaced>
              <ColumnSectionSpaced>
                <TextFieldWithInfo
                  label="topK"
                  type="number"
                  inputProps={{ step: 1, min: 10, max: 500 }}
                  value={config.general.topK ?? 50}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const raw = e.target.value;
                    const parsed = parseInt(raw, 10);
                    const next = Number.isFinite(parsed) ? parsed : 50;
                    setConfig((prev) => ({
                      ...prev,
                      general: { ...prev.general, topK: next },
                    }));
                  }}
                  tooltip="Max neighbors evaluated per seed. Higher explores more candidates. Default 50."
                />
              </ColumnSectionSpaced>
            </TwoColumnGrid>
          </CardContent>
        </FullWidthConfigCard>
      </FullWidthWrapper>

      <ActionButtons>
        <StartButton
          variant="contained"
          color="primary"
          size="large"
          onClick={handleStartGuessing}
          disabled={!canStart}
        >
          Start Semantic Unifier
        </StartButton>
      </ActionButtons>
    </Wrapper>
  );
};

export default MemorySemanticUnifierConfig;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`;

const Header = styled.div`
  padding: 20px 20px 0 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const Title = styled(Typography).attrs({ variant: 'h5' })`
  && {
    margin-bottom: 8px;
    color: #333;
  }
`;

const Description = styled(Typography).attrs({ variant: 'body2' })`
  && {
    color: #666;
    margin-bottom: 20px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FullWidthWrapper = styled.div`
  padding: 0 20px;
  margin-bottom: 16px;
`;

const SectionTitle = styled(Typography).attrs({ variant: 'h6' })`
  && {
    color: #444;
    margin: 0;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
`;

const ColumnSection = styled.div``;
const ColumnSectionSpaced = styled(ColumnSection)`
  margin-top: 40px;
`;

const FullWidthConfigCard = styled(Card)`
  && {
    width: 100%;
    box-shadow: none;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    &:hover {
      border-color: #bdbdbd;
    }
  }
`;

const CollapsibleContent = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const SectionDescription = styled.div`
  color: #666;
  font-size: 14px;
  margin-top: 12px;
  margin-bottom: 20px;
  line-height: 1.4;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 20px 20px 20px;
  border-top: 1px solid #e0e0e0;
  margin-top: 24px;
`;

const StartButton = styled(Button)`
  && {
    background: #1976d2;
    border-radius: 6px;
    padding: 12px 32px;
    font-size: 16px;
    font-weight: 600;
    text-transform: none;
    border: 1px solid #1976d2;
    &:hover {
      background: #1565c0;
      border-color: #1565c0;
    }
  }
`;
